<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    loading-message="Loading Active Rides..."
    no-action
  >
    <template #title>
      Active Rides
    </template>

    <template #content>
      <message-html :result-html="status.resultHtml" />

      <div class="row align-items-center">
        <pagination
          :is-loading="status.isLoading"
          :current-page="paginationInfo.currentPage"
          :total-pages="paginationInfo.totalPages"
          @load-page-data="loadActiveRides"
        />
      </div>

      <table-view-container
        :page-id="pageId"
        :data="rides"
        :table-columns="tableColumns"
      />
    </template>
  </card-container>
</template>

<script lang="ts">
import { ref, Ref, reactive, onMounted } from 'vue';

import util from '@/Utils/util';
import rideUtility from '@/Utils/rideUtility';
import { usePartner } from '@/use/partner';
import { TableColumnType } from '@/models/table';
import { Ride, ExtendedRide } from '@/models/ride';
import { rideService } from '@/services/ride';

const pageId = 'partner-active-rides';
const paginationLimit = 20;

const formatMapLink = (gogoMap: string) => {
  if (!gogoMap) {
    return '';
  }

  return `<a href="${gogoMap}" target="_blank" style="display: block; text-align: center;">View Map</a>`;
};

const tableColumns: TableColumnType[] = [
  { label: 'No', field: 'index', width: 60, isCenterAlign: true },
  { label: 'Customer', field: 'customerName', width: 150 },
  { label: 'Phone Number', field: 'riderPhone', width: 120 },
  { label: 'Driver Phone Number', field: 'driverPhone', width: 120 },
  { label: 'Map', field: 'gogoMap', width: 120, formatter: formatMapLink },
  { label: 'Created At', field: 'createdAt', width: 180, isDateField: true },
  { label: 'Time Accepted', field: 'timeAccepted', width: 180, isDateField: true },
  { label: 'Time Arriving', field: 'timeArriving', width: 180, isDateField: true },
  { label: 'Time In Progress', field: 'timeInProgress', width: 180, isDateField: true },
  { label: 'Request ID', field: 'requestId', width: 280 },
  { label: 'Start Location', field: 'startLocation', width: 270 },
  { label: 'End Location', field: 'endLocation', width: 270 },
  { label: 'Destination', field: 'destination', width: 270 },
  { label: 'Distance', field: 'distance', width: 100 },
  { label: 'Duration', field: 'duration', width: 100 },
  { label: 'Total Fare', field: 'gogoCost', width: 100 },
  { label: 'Customer Cost', field: 'customerCost', width: 120 },
  { label: 'Customer Credits', field: 'customerCredits', width: 150 },
  { label: 'Partner Cost', field: 'partnerCost', width: 120 },
  { label: 'Partner Credits', field: 'partnerCredits', width: 150 },
  { label: 'Surge Factor', field: 'surgeFactor', width: 120 },
];

export default {
  name: 'ActiveRides',

  setup: () => {
    const { partnerId, partnerInfo } = usePartner();

    const rides: Ref<ExtendedRide[]> = ref([]);

    const status = reactive({
      isLoading: false,
      resultHtml: '',
    });
    const paginationInfo = reactive({
      currentPage: 1,
      totalPages: 1,
    });

    const loadActiveRides = async (currentPage: number = 1) => {
      const offset = (currentPage - 1) * paginationLimit;
      paginationInfo.currentPage = currentPage;

      status.resultHtml = '';
      status.isLoading = true;
      try {
        const response = await rideService.getPartnerActiveRides({
          partnerId: partnerId.value,
          pagination: {
            limit: paginationLimit,
            offset,
          },
        });

        rides.value = response.rows.map((ride: Ride, index: number) => {
          const formattedData = rideUtility.formatRideData(ride, false, partnerInfo.value.timeZone);
          formattedData.index = (currentPage - 1) * paginationLimit + index + 1;
          formattedData.customerName = ride.caller ? `${ride.caller.firstName} ${ride.caller.lastName}` : 'N/A';

          return formattedData;
        });

        paginationInfo.totalPages = Math.ceil(response.count / paginationLimit) || 1;
      } catch (error) {
        rides.value = [];

        paginationInfo.currentPage = 1;
        paginationInfo.totalPages = 1;

        status.resultHtml = util.getGraphqlError(error);
      }
      status.isLoading = false;
    };

    onMounted(loadActiveRides);

    return {
      pageId,
      tableColumns,
      rides,
      status,
      paginationInfo,

      loadActiveRides,
    };
  },
};
</script>
